import React from 'react';
import { Table } from 'react-bootstrap';
import { Order, OrderStatus } from '../../../../api/shop/types/orderTypes';
import { OrderStatusBadge } from '../../../../Common/BadgeStyles';
import '../../../../Common/BadgeStyles.css'; // Ensure badge styles are loaded
import '../../../../Common/ManageTournamentsFixturesTable.css'; // Table styling
import './ShopServicesTable.css'; // Shop Services specific table overrides
import ShimmerLoader from '../../../../Common/ShimmerLoader';

interface OrdersTableProps {
  orders: Order[];
  loading: boolean;
  pagination: {
    page: number;
    totalPages: number;
    totalCount: number;
  };
  onPageChange: (page: number) => void;
  onViewOrder?: (orderId: string) => void;
}

const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  loading,
  pagination,
  onPageChange,
  onViewOrder,
}) => {
  const formatPrice = (price: number) => {
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return <ShimmerLoader type="table" rows={5} />;
  }

  if (orders.length === 0) {
    return (
      <div className="text-center py-5">
        <p className="text-muted">No orders found</p>
      </div>
    );
  }

  return (
    <div className="manage-tournaments-table-container">
      <Table responsive hover className="mb-0 manage-tournaments-table shop-services-table">
        <thead>
          <tr>
            <th>Product Name</th>
            <th>Ordered by</th>
            <th>Order Date</th>
            <th>Price</th>
            <th>Order Status</th>
            <th>Order Link</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order._id}>
              <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{order.productName}</td>
              <td>{order.orderedBy}</td>
              <td>{formatDate(order.orderDate)}</td>
              <td>{formatPrice(order.price)}</td>
              <td>
                <OrderStatusBadge status={order.orderStatus} />
              </td>
              <td>
                {order.orderLink ? (
                  <a
                    href={order.orderLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: '#006EFF', textDecoration: 'none' }}
                  >
                    View Order
                  </a>
                ) : order.orderId || order._id ? (
                  <a
                    href={`/shop-services/orders/${order.orderId || order._id}`}
                    style={{ color: '#006EFF', textDecoration: 'none' }}
                  >
                    View Order
                  </a>
                ) : onViewOrder ? (
                  <button
                    className="btn btn-link p-0"
                    onClick={() => onViewOrder(order._id)}
                    style={{ color: '#006EFF', textDecoration: 'none' }}
                  >
                    View Order
                  </button>
                ) : (
                  <span className="text-muted">-</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div className="text-muted">
            Showing {((pagination.page - 1) * 10) + 1} - {Math.min(pagination.page * 10, pagination.totalCount)} of {pagination.totalCount} orders
          </div>
          <div className="d-flex gap-2">
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onPageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
            >
              Previous
            </button>
            <span className="d-flex align-items-center px-3">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onPageChange(pagination.page + 1)}
              disabled={pagination.page >= pagination.totalPages}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersTable;
